import type { EventBus } from "../core/EventBus.ts";
import type {
  EnemyConfig,
  EnemyDataFile,
  WaveDataFile,
} from "../entities/types.ts";
import { GAME } from "../core/config.ts";

/**
 * BL-156 / D-254 (S157) — the ENEMY BRAIN.
 *
 * The owner's directive: *"Das muss echt das Enemy Brain werden — klar
 * durchdachte Logik wie launches erfolgen."* This is the single, central,
 * data-driven authority over the launch DECISIONS — *what comes at the player,
 * when, in what combination, and how it is introduced*. The proven low-level
 * spawn-tick / RNG plumbing stays in {@link WaveManager}; the Brain owns the
 * decisions + the introduction + the documented composition model (the parts
 * that were scattered across `waves.json` + `WaveManager` + a hardcoded
 * allowlist). See `docs/design/ENEMY_BRAIN.md`.
 *
 * It ships **byte-identical**: the introduction telegraph fires on exactly the
 * same waves, for exactly the same archetypes, as the retired
 * `NEW_THREAT_TYPES = ["jammer","overseer"]` allowlist — only now the roster
 * lives in DATA (`enemies.json` `special.newThreat`), the schedule DERIVES from
 * the authored `waves.json`, and the composition role model is a documented,
 * read-only lens (it drives no spawn, no RNG, no event).
 */

/**
 * The composition role taxonomy — the "clearly-thought-out logic" lens over the
 * authored wave mix. Each role names *what an archetype does to the fight*; the
 * roles drive NO behaviour (a pure structural view), so the classification can
 * evolve with zero sim impact. See {@link classifyRole} for the derivation.
 */
export type EnemyRole =
  | "apex" // boss-tier climax (multi-phase / spawns adds)
  | "anchor" // the wall: high-HP, slow grinders (incl. firewall-breakers)
  | "flanker" // slips the line: stealth / shape-shifting / evasive
  | "support" // alters the fight for the enemy side (buffs allies / suppresses towers)
  | "pressure"; // cheap, numerous swarm — overwhelm by count

/**
 * Classify an archetype's composition ROLE from its `enemies.json` data
 * (first-match-wins). DERIVED from the data fields — NOT a hardcoded enemy list
 * — so a new archetype is classified automatically from its stats. Documented
 * in `docs/design/ENEMY_BRAIN.md` §1.2. Read-only: byte-neutral.
 */
export function classifyRole(cfg: EnemyConfig): EnemyRole {
  const t = cfg.special?.type;
  if (t === "finalBoss" || t === "spawnAdds") return "apex";
  // BL-155 (S158) — Threat Pack 2 axes: the Leech disrupts the enemy side's
  // favour (drains YOUR economy) like the jammer/overseer → support; the
  // Regenerator is the burst-or-bust wall → anchor; the Splitter becomes a swarm
  // → pressure (forces AoE). Read-only lens (drives no spawn/RNG) — byte-neutral.
  if (t === "overseer" || t === "jammer" || t === "leech") return "support";
  if (t === "regenerator") return "anchor";
  if (t === "splitter") return "pressure";
  if (cfg.stealth === true || t === "shiftResistance") return "flanker";
  if ((cfg.firewallDamage ?? 0) > 0 || cfg.hp >= 150) return "anchor";
  return "pressure";
}

export class EnemyDirector {
  private readonly waveData: WaveDataFile;
  private readonly enemyData: EnemyDataFile;
  private readonly bus: EventBus;

  /**
   * The once-per-archetype "NEW THREAT DETECTED" guard (the introduce-at-wave-N
   * cadence). A flagged archetype that appears in a wave's spawn queue fires its
   * telegraph exactly once per run; cleared on {@link reset}. Keyed on
   * `special.type` so the registry + the telegraph stay in lockstep — the exact
   * relocation of the old `WaveManager.announcedThreats_`.
   */
  private readonly announcedThreats_ = new Set<string>();

  /** The roster of telegraphed "new threats" (archetype id → `special.type`),
   *  derived once from the `newThreat` DATA flag — the data-driven replacement
   *  for the hardcoded allowlist. */
  private readonly newThreatRoster_: ReadonlyMap<string, string>;

  /** Each archetype's debut wave, derived once from the authored `waves.json`
   *  composition (first appearance). The documented introduction schedule. */
  private readonly introductionSchedule_: ReadonlyMap<string, number>;

  constructor(waveData: WaveDataFile, enemyData: EnemyDataFile, bus: EventBus) {
    this.waveData = waveData;
    this.enemyData = enemyData;
    this.bus = bus;
    this.newThreatRoster_ = this.buildRoster();
    this.introductionSchedule_ = this.buildIntroductionSchedule();
  }

  /** Clear the once-per-run introduction state (mirrors `WaveManager.reset`). */
  reset(): void {
    this.announcedThreats_.clear();
  }

  // ── Decision 4: the launch orchestration (the introduction telegraph) ──────

  /**
   * The once-per-run "NEW THREAT DETECTED" decision — the exact relocation of
   * the scan that used to live inline in `WaveManager.startWave`. Scans the
   * wave's spawn queue for a DATA-flagged new archetype (`special.newThreat`)
   * making its FIRST appearance this run and emits `wave.newthreat.incoming`
   * ONCE per mechanic (keyed on `special.type`). The banner copy (EN+DE) + the
   * audio stinger live in the UI/audio handlers. Superseded the retired hardcoded
   * `NEW_THREAT_TYPES` allowlist; the flagged set is DATA-driven and today is five
   * (jammer, overseer, leech, regenerator, splitter — all EN+DE telegraphed).
   */
  announceNewThreats(wave: number, spawnQueue: { type: string }[]): void {
    const newThreats: string[] = [];
    for (const e of spawnQueue) {
      if (e.type === "__delay__") continue;
      const special = this.enemyData[e.type]?.special;
      if (
        special?.newThreat &&
        !this.announcedThreats_.has(special.type)
      ) {
        this.announcedThreats_.add(special.type);
        if (!newThreats.includes(special.type)) newThreats.push(special.type);
      }
    }
    if (newThreats.length > 0) {
      this.bus.emit("wave.newthreat.incoming", { wave, types: newThreats });
    }
  }

  // ── Decision 1: the roster & introduction schedule (data-driven) ───────────

  /** Is this archetype a telegraphed "new threat" (carries `special.newThreat`)? */
  isNewThreat(type: string): boolean {
    return this.newThreatRoster_.has(type);
  }

  /** The telegraphed-roster archetypes → their debut wave (the introduction
   *  schedule for exactly the threats that fire the banner). */
  rosterDebuts(): ReadonlyMap<string, number> {
    const out = new Map<string, number>();
    for (const id of this.newThreatRoster_.keys()) {
      const debut = this.introductionSchedule_.get(id);
      if (debut !== undefined) out.set(id, debut);
    }
    return out;
  }

  /** The wave an archetype debuts (first appears in the authored composition),
   *  or `undefined` if it never appears. The documented introduction schedule. */
  debutWaveOf(type: string): number | undefined {
    return this.introductionSchedule_.get(type);
  }

  // ── Decision 2: the composition role model (the documented lens) ───────────

  /** The composition ROLE of an archetype (DERIVED from its data). */
  roleOf(type: string): EnemyRole | undefined {
    const cfg = this.enemyData[type];
    return cfg ? classifyRole(cfg) : undefined;
  }

  /**
   * The per-role headcount of a wave's authored composition — the legible answer
   * to "what launches together, and why." Reads the authored `waves.json` groups
   * (it does NOT re-author them) and classifies each group's type by role.
   */
  composeWave(wave: number): Record<EnemyRole, number> {
    const tally: Record<EnemyRole, number> = {
      pressure: 0,
      flanker: 0,
      anchor: 0,
      support: 0,
      apex: 0,
    };
    const template = this.waveData.templates.find((t) => t.wave === wave);
    if (!template) return tally;
    for (const g of template.groups) {
      const role = this.roleOf(g.type);
      if (role) tally[role] += g.count;
    }
    return tally;
  }

  // ── Decision 3: the cadence / escalation (per-act) ─────────────────────────

  /** The act a wave belongs to (1/2/3), from the central act boundaries. */
  actOf(wave: number): 1 | 2 | 3 {
    if (wave <= GAME.WAVES_ACT_I) return 1;
    if (wave <= GAME.WAVES_ACT_II) return 2;
    return 3;
  }

  // ── Derivations (computed once at construction) ────────────────────────────

  /** The telegraphed roster from the `newThreat` DATA flag (id → `special.type`). */
  private buildRoster(): ReadonlyMap<string, string> {
    const roster = new Map<string, string>();
    for (const [id, cfg] of Object.entries(this.enemyData)) {
      if (cfg.special?.newThreat) roster.set(id, cfg.special.type);
    }
    return roster;
  }

  /** Each archetype's debut wave = its first appearance scanning the authored
   *  templates in ascending wave order. */
  private buildIntroductionSchedule(): ReadonlyMap<string, number> {
    const schedule = new Map<string, number>();
    const templates = [...this.waveData.templates].sort(
      (a, b) => a.wave - b.wave,
    );
    for (const template of templates) {
      for (const g of template.groups) {
        if (!schedule.has(g.type)) schedule.set(g.type, template.wave);
      }
    }
    return schedule;
  }
}
